import React, { useCallback, useEffect, useState } from "react";

import LoadingSpinner from "./LoadingSpinner";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { fmtUtc } from "../lib/format";
import {
  type SimSlotName,
  type SimStatus,
  type SimSwitchLogRow,
  fetchSimStatus,
  fetchSimSwitchLog,
  requestSimSwitch,
} from "../services/simSwitchApi";

/**
 * Per-box SIM card. Shows both SIM slots with carrier + active flag
 * and offers a "Switch to SIM X" button per row.
 *
 * IMPORTANT: SIM switching is DEFAULT-DISABLED server-side
 * (`MttrSettings.PeplinkSimSwitchEnabled=false`). The button is
 * always rendered but is DISABLED whenever the operator can't do
 * anything productive — the flag being off is one such case, but
 * we don't fetch that state up front; instead we let the operator
 * attempt the click and the server explains why with an HTTP 501
 * body. That way, flipping the flag on later Just Works without a
 * frontend redeploy.
 */
interface SimControlCardProps {
  scuName: string;
}

export const SimControlCard: React.FC<SimControlCardProps> = ({ scuName }) => {
  const { token, can } = useAuth();
  const canOperate = can("operator.triage");
  const [status, setStatus] = useState<SimStatus | null>(null);
  const [logs, setLogs] = useState<SimSwitchLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<SimSlotName | null>(null);
  const [lastMessage, setLastMessage] = useState<string | null>(null);

  const load = useCallback((): void => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      fetchSimStatus(token, scuName).catch(() => null),
      fetchSimSwitchLog(token, scuName).catch(() => ({ data: [] as SimSwitchLogRow[] })),
    ])
      .then(([s, l]) => {
        setStatus(s);
        setLogs(l.data);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [token, scuName]);

  useEffect(() => { load(); }, [load]);

  const doSwitch = useCallback(async (to: SimSlotName): Promise<void> => {
    if (!token) return;
    setPending(to);
    setLastMessage(null);
    try {
      const { status: httpStatus, body } = await requestSimSwitch(token, scuName, to);
      if (httpStatus === 200 && body.ok) {
        setLastMessage(`Switched to SIM ${to}. Give the Peplink 30–60 s to re-attach.`);
      } else if (httpStatus === 501) {
        setLastMessage(
          body.detail
            ? `SIM switching is disabled: ${body.detail}`
            : `SIM switching is not enabled server-side (default). Flip MttrSettings.PeplinkSimSwitchEnabled once the wire spec is confirmed.`,
        );
      } else if (httpStatus === 502) {
        setLastMessage(
          `Server couldn't reach the Peplink to switch (${body.error ?? "unreachable"}).`,
        );
      } else if (httpStatus === 400 && /already the active slot/i.test(body.error ?? "")) {
        setLastMessage(`SIM ${to} is already the active slot.`);
      } else {
        setLastMessage(body.error ?? `Unexpected response (HTTP ${httpStatus}).`);
      }
      load();  // refresh status + log
    } catch (e) {
      setLastMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setPending(null);
    }
  }, [token, scuName, load]);

  return (
    <div className="bg-white border border-secondary-200 rounded-md p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold">SIM slots</h3>
        {status && (
          <span className="text-xs text-secondary-500 font-mono">
            {status.device_sn}
          </span>
        )}
      </div>

      {loading && !status ? (
        <div className="py-6 flex justify-center"><LoadingSpinner /></div>
      ) : error ? (
        <div className="text-xs text-error-700">{error}</div>
      ) : !status || status.slots.length === 0 ? (
        <div className="text-xs text-secondary-500 py-2">
          No cached SIM status for this Peplink yet. It'll populate on the
          next bandwidth-runner cycle or once a client-list poll succeeds.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(["A", "B"] as SimSlotName[]).map((slot) => {
            const s = status.slots.find((r) => r.slot === slot);
            const isActive = s?.is_active ?? false;
            return (
              <div
                key={slot}
                className={`border rounded-md p-3 ${
                  isActive
                    ? "border-success-300 bg-success-50/40"
                    : "border-secondary-200 bg-secondary-50"
                }`}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-secondary-600">
                    SIM {slot}
                  </span>
                  {isActive && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-success-600 text-white">
                      active
                    </span>
                  )}
                </div>
                <div className="text-sm font-semibold">
                  {s?.carrier ?? <span className="text-secondary-400">no data</span>}
                </div>
                <div className="text-[10px] text-secondary-500 font-mono">
                  {s?.iccid ? `ICCID ${s.iccid.slice(-8)}` : ""}
                  {s?.sim_kind && ` · ${s.sim_kind}`}
                  {s?.signal_dbm != null && ` · ${s.signal_dbm} dBm`}
                </div>
                {s?.last_seen_utc && (
                  <div className="text-[10px] text-secondary-400 font-mono mt-0.5">
                    seen {fmtUtc(s.last_seen_utc)} · {s.source}
                  </div>
                )}
                {canOperate && !isActive && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2 w-full"
                    disabled={pending !== null}
                    onClick={() => void doSwitch(slot)}
                    title="Server returns 501 while SIM switching is disabled — attempting is safe."
                  >
                    {pending === slot ? "Switching…" : `Switch to SIM ${slot}`}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {lastMessage && (
        <div className="mt-3 text-xs text-secondary-700 bg-secondary-50 border border-secondary-200 rounded px-3 py-2">
          {lastMessage}
        </div>
      )}

      {logs.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider text-secondary-500 font-semibold mb-1">
            Recent switch attempts
          </div>
          <ul className="text-xs divide-y divide-secondary-100 border border-secondary-200 rounded-md">
            {logs.slice(0, 5).map((row) => (
              <li key={row.log_id} className="px-3 py-1.5 flex items-baseline gap-2">
                <span className="font-mono text-secondary-600">
                  {row.requested_utc ? fmtUtc(row.requested_utc) : "?"}
                </span>
                <span className="font-mono text-secondary-800">
                  {row.from_sim ?? "?"} → {row.to_sim}
                </span>
                <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  row.outcome === "success"
                    ? "bg-success-100 text-success-800"
                    : row.outcome === "not_enabled"
                      ? "bg-secondary-200 text-secondary-700"
                      : "bg-warning-100 text-warning-900"
                }`}>
                  {row.outcome}
                </span>
                {row.requested_by_email && (
                  <span className="text-[10px] text-secondary-500 truncate max-w-[140px]">
                    {row.requested_by_email}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SimControlCard;
